/**
 * EventOverviewSection — customer-fidelity event summary.
 *
 * Renders a calm one-line summary of the event identity + date + city.
 * Hides admin-only fields entirely; the projection layer already strips
 * them but we treat the customer payload as authoritative.
 */

import React from 'react';
import { Calendar, MapPin, Users } from 'lucide-react';

function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch (_) {
    return null;
  }
}

export default function EventOverviewSection({ event }) {
  if (!event) return null;
  const date = fmtDate(event.event_date);
  const location = event.location_label;
  const guests = event.guest_count;
  const days = event.days_until_event;
  // B-EXP-6.21 — canonical event identity. `title` is the customer-facing
  // event name edited via the Concierge Case Workspace → Event Details
  // edit flow. Falls back to `event_type` for legacy events without a
  // title set, and finally to the generic "Event" placeholder.
  const displayTitle = (event.title && event.title.trim())
    || event.event_type
    || 'Event';

  return (
    <div
      className="rounded-xl bg-gray-50/70 border border-gray-100 p-4"
      data-testid="ops-event-overview"
    >
      <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">
        Event
      </p>
      <p className="text-sm font-semibold text-gray-900" data-testid="ops-event-title">
        {displayTitle}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-gray-600">
        {date && (
          <span className="inline-flex items-center gap-1" data-testid="ops-event-date">
            <Calendar className="w-3.5 h-3.5 text-gray-400" aria-hidden /> {date}
          </span>
        )}
        {location && (
          <span className="inline-flex items-center gap-1" data-testid="ops-event-location">
            <MapPin className="w-3.5 h-3.5 text-gray-400" aria-hidden /> {location}
          </span>
        )}
        {guests != null && (
          <span className="inline-flex items-center gap-1" data-testid="ops-event-guests">
            <Users className="w-3.5 h-3.5 text-gray-400" aria-hidden /> {guests} guests
          </span>
        )}
      </div>
      {Number.isFinite(days) && days >= 0 && (
        <p className="mt-2 text-xs text-gray-500" data-testid="ops-event-countdown">
          {days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `in ${days} days`}
        </p>
      )}
    </div>
  );
}
